import { Info, Keyboard, LifeBuoy } from 'lucide-react';
import { useState } from 'react';

import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Hint } from '@/components/ui/tooltip';

/**
 * Help menu.
 *
 * The shortcut sheet is the part that earns its place: an ERP is operated by
 * people who use it all day, and they will learn the keys if the app tells them
 * what they are.
 */

const SHORTCUTS: { keys: string[]; action: string }[] = [
  { keys: ['Ctrl', 'K'], action: 'Open search and jump to a module' },
  { keys: ['Ctrl', 'B'], action: 'Collapse or expand the sidebar' },
  { keys: ['Esc'], action: 'Close the open dialog or panel' },
  { keys: ['Enter'], action: 'Submit the focused form' },
  { keys: ['/'], action: 'Focus the list search box' },
];

export const HelpMenu = () => {
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <Hint label="Help">
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Help"
              className="hidden text-muted-foreground sm:inline-flex"
            >
              <LifeBuoy />
            </Button>
          </DropdownMenuTrigger>
        </Hint>

        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Help</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setShortcutsOpen(true)}>
            <Keyboard />
            Keyboard shortcuts
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setAboutOpen(true)}>
            <Info />
            About this build
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Modal
        open={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
        title="Keyboard shortcuts"
        description="Shortcuts work anywhere except inside a text field."
        size="md"
      >
        <dl className="divide-y divide-border">
          {SHORTCUTS.map((shortcut) => (
            <div key={shortcut.action} className="flex items-center justify-between gap-4 py-2.5">
              <dt className="text-sm text-muted-foreground">{shortcut.action}</dt>
              <dd className="flex shrink-0 items-center gap-1">
                {shortcut.keys.map((key) => (
                  <kbd
                    key={key}
                    className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-2xs font-medium"
                  >
                    {key}
                  </kbd>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </Modal>

      <Modal
        open={aboutOpen}
        onOpenChange={setAboutOpen}
        title="About this build"
        size="sm"
        description="Quote this when reporting a problem."
      >
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Version</dt>
            <dd className="font-mono">{__APP_VERSION__}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Environment</dt>
            <dd className="font-mono">{import.meta.env.MODE}</dd>
          </div>
        </dl>
      </Modal>
    </>
  );
};
